import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { SeFeMenuComponent, SeFeMenuOptions } from 'se-fe-menu'
import { SeFeTranslationsLoader } from 'se-fe-translations'

export interface OpenRegistrationFilterOption {
  id: string
  name: string
}

export interface OpenRegistrationFilters {
  sport: string[]
  type: string[]
  organization: string[]
  person?: string[]
}

export type OpenRegistrationFilterKey = keyof OpenRegistrationFilters
type CheckboxOptionsById = Record<string, { label: string; value: boolean }[]>

export interface OpenRegistrationFilterAction {
  action: 'apply' | 'reset'
  filterKey: OpenRegistrationFilterKey
}

@Component({
  selector: 'se-po-open-registration-filter',
  templateUrl: './open-registration-filter.component.html',
  styleUrls: ['./open-registration-filter.component.scss'],
  standalone: false,
})
export class OpenRegistrationFilterComponent implements OnChanges, OnInit {
  public readonly translationKey =
    'SE_PO_SE_REGISTRATION_UI_FEATURE_OPEN_REGISTRATION_LIST.'
  private readonly translationsPath =
    'se-registration-ui/feature-open-registration-list/assets/translations'

  @Input() public sports: OpenRegistrationFilterOption[] = []
  @Input() public signupTypeList: OpenRegistrationFilterOption[] = []
  @Input() public organizations: OpenRegistrationFilterOption[] = []
  @Input() public people: OpenRegistrationFilterOption[] = []
  @Input() public showPersonFilter = false
  @Input() public filters: OpenRegistrationFilters | undefined

  @Output() public filtersChange = new EventEmitter<OpenRegistrationFilters>()
  @Output() public filterAction = new EventEmitter<OpenRegistrationFilterAction>()

  public readonly filtersForm = new UntypedFormGroup({
    sport: new UntypedFormGroup({}),
    organization: new UntypedFormGroup({}),
    type: new UntypedFormGroup({}),
    person: new UntypedFormGroup({}),
  })
  public readonly sportMenuOptions: SeFeMenuOptions = {
    name: 'sportMenu',
    title: 'Sport',
    maxHeight: '50vh',
    minWidth: '280px',
  }
  public readonly organizationMenuOptions: SeFeMenuOptions = {
    name: 'organizationMenu',
    title: 'Organization',
    maxHeight: '50vh',
    minWidth: '280px',
  }
  public readonly typeMenuOptions: SeFeMenuOptions = {
    name: 'typeMenu',
    title: 'Type',
    maxHeight: '50vh',
    minWidth: '280px',
  }
  public readonly personMenuOptions: SeFeMenuOptions = {
    name: 'personMenu',
    title: 'Person',
    maxHeight: '50vh',
    minWidth: '280px',
  }

  public selectedSportIds: string[] = []
  public selectedOrganizationIds: string[] = []
  public selectedTypeIds: string[] = []
  public selectedPersonIds: string[] = []
  public sportCheckboxOptions: CheckboxOptionsById = {}
  public organizationCheckboxOptions: CheckboxOptionsById = {}
  public typeCheckboxOptions: CheckboxOptionsById = {}
  public personCheckboxOptions: CheckboxOptionsById = {}
  public translationsLoaded = false

  public get sportForm(): UntypedFormGroup {
    return this.filtersForm.get('sport') as UntypedFormGroup
  }

  public get organizationForm(): UntypedFormGroup {
    return this.filtersForm.get('organization') as UntypedFormGroup
  }

  public get personForm(): UntypedFormGroup {
    return this.filtersForm.get('person') as UntypedFormGroup
  }

  public get sportFilterDisabled(): boolean {
    return this.sports.length === 0
  }

  public get organizationFilterDisabled(): boolean {
    return this.organizations.length === 0
  }

  public get typeFilterDisabled(): boolean {
    return this.signupTypeList.length === 0
  }

  public get personFilterVisible(): boolean {
    return this.showPersonFilter || this.people.length > 0
  }

  public get personFilterDisabled(): boolean {
    return this.people.length === 0
  }

  public get typeForm(): UntypedFormGroup {
    return this.filtersForm.get('type') as UntypedFormGroup
  }

  public get sportFilterCount(): number {
    return this.selectedSportIds.length
  }

  public get organizationFilterCount(): number {
    return this.selectedOrganizationIds.length
  }

  public get typeFilterCount(): number {
    return this.selectedTypeIds.length
  }

  public get sportChipText(): string {
    return this.translationKey + 'FILTER.sport'
  }

  public get typeChipText(): string {
    return this.translationKey + 'FILTER.type'
  }

  public get organizationChipText(): string {
    return this.translationKey + 'FILTER.organization'
  }

  public get personChipText(): string {
    return this.translationKey + 'FILTER.person'
  }

  constructor(private translationsLoader: SeFeTranslationsLoader) {}

  public ngOnInit(): void {
    void this.loadTranslations()
  }

  public ngOnChanges(changes?: SimpleChanges): void {
    if (changes?.filters) {
      this.setSelectedIdsFromFilters(this.filters)
    }

    this.syncSelectedValuesWithOptions()
  }

  public hasSelectedValues(selectedIds: string[]): boolean {
    return selectedIds.length > 0
  }

  public applyFilter(filterKey: OpenRegistrationFilterKey, menu?: SeFeMenuComponent): void {
    const nextSelectedIds = this.selectedIdsFromControlValues(
      this.filterForm(filterKey).value,
      this.filterOptions(filterKey)
    )

    if (this.selectedIdsChanged(this.selectedIds(filterKey), nextSelectedIds)) {
      this.setSelectedIds(filterKey, nextSelectedIds)
      this.emitFilters()
    }

    this.emitFilterAction('apply', filterKey)
    menu?.closeMenu()
  }

  public clearFilter(filterKey: OpenRegistrationFilterKey, menu?: SeFeMenuComponent): void {
    this.clearFilterSelection(filterKey)
    this.emitFilters()
    this.emitFilterAction('reset', filterKey)
    menu?.closeMenu()
  }

  public clearAllFilters(emitFilters = true): void {
    const filterKeys: OpenRegistrationFilterKey[] = ['sport', 'organization', 'type', 'person']

    filterKeys.forEach((filterKey) => {
      this.clearFilterSelection(filterKey)
    })

    if (emitFilters) this.emitFilters()
  }

  private syncSelectedValuesWithOptions(): void {
    const previousSportIds = this.selectedSportIds
    const previousOrganizationIds = this.selectedOrganizationIds
    const previousTypeIds = this.selectedTypeIds
    const previousPersonIds = this.selectedPersonIds

    this.selectedSportIds = this.keepExistingSelections(this.selectedSportIds, this.sports)
    this.syncCheckboxControls(this.sportForm, this.sports, this.selectedSportIds)
    this.sportCheckboxOptions = this.toCheckboxOptions(this.sports)

    this.selectedOrganizationIds = this.keepExistingSelections(
      this.selectedOrganizationIds,
      this.organizations
    )
    this.syncCheckboxControls(this.organizationForm, this.organizations, this.selectedOrganizationIds)
    this.organizationCheckboxOptions = this.toCheckboxOptions(this.organizations)

    this.selectedTypeIds = this.keepExistingSelections(this.selectedTypeIds, this.signupTypeList)
    this.syncCheckboxControls(this.typeForm, this.signupTypeList, this.selectedTypeIds)
    this.typeCheckboxOptions = this.toCheckboxOptions(this.signupTypeList)

    this.selectedPersonIds = this.keepExistingSelections(this.selectedPersonIds, this.people)
    this.syncCheckboxControls(this.personForm, this.people, this.selectedPersonIds)
    this.personCheckboxOptions = this.toCheckboxOptions(this.people)

    if (this.selectionChanged(
      previousSportIds,
      previousOrganizationIds,
      previousTypeIds,
      previousPersonIds
    )) {
      this.emitFilters()
    }
  }

  private keepExistingSelections(selectedIds: string[], options: OpenRegistrationFilterOption[]): string[] {
    const optionIds = new Set(options.map(({ id }) => id))
    return selectedIds.filter((id) => optionIds.has(id))
  }

  private setSelectedIdsFromFilters(filters?: OpenRegistrationFilters): void {
    this.selectedSportIds = [...(filters?.sport || [])]
    this.selectedOrganizationIds = [...(filters?.organization || [])]
    this.selectedTypeIds = [...(filters?.type || [])]
    this.selectedPersonIds = [...(filters?.person || [])]
  }

  private syncCheckboxControls(
    group: UntypedFormGroup,
    options: OpenRegistrationFilterOption[],
    selectedIds: string[]
  ): void {
    const optionIds = new Set(options.map(({ id }) => id))
    Object.keys(group.controls).forEach((id) => {
      if (!optionIds.has(id)) group.removeControl(id, { emitEvent: false })
    })

    options.forEach(({ id }) => {
      const selected = selectedIds.includes(id)
      if (group.contains(id)) {
        group.get(id).setValue(selected, { emitEvent: false })
      } else {
        group.addControl(id, new UntypedFormControl(selected), { emitEvent: false })
      }
    })
  }

  private selectedIdsFromControlValues(
    values: Record<string, boolean>,
    options: OpenRegistrationFilterOption[]
  ): string[] {
    return options
      .filter(({ id }) => values?.[id])
      .map(({ id }) => id)
  }

  private toCheckboxOptions(options: OpenRegistrationFilterOption[]): CheckboxOptionsById {
    return options.reduce((checkboxOptions, { id, name }) => {
      checkboxOptions[id] = [{ label: name, value: true }]
      return checkboxOptions
    }, {} as CheckboxOptionsById)
  }

  private filterForm(filterKey: OpenRegistrationFilterKey): UntypedFormGroup {
    const forms: Record<OpenRegistrationFilterKey, UntypedFormGroup> = {
      sport: this.sportForm,
      organization: this.organizationForm,
      type: this.typeForm,
      person: this.personForm,
    }
    return forms[filterKey]
  }

  private filterOptions(filterKey: OpenRegistrationFilterKey): OpenRegistrationFilterOption[] {
    const options: Record<OpenRegistrationFilterKey, OpenRegistrationFilterOption[]> = {
      sport: this.sports,
      organization: this.organizations,
      type: this.signupTypeList,
      person: this.people,
    }
    return options[filterKey]
  }

  private setSelectedIds(filterKey: OpenRegistrationFilterKey, ids: string[]): void {
    if (filterKey === 'sport') this.selectedSportIds = ids
    if (filterKey === 'organization') this.selectedOrganizationIds = ids
    if (filterKey === 'type') this.selectedTypeIds = ids
    if (filterKey === 'person') this.selectedPersonIds = ids
  }

  private clearFilterSelection(filterKey: OpenRegistrationFilterKey): void {
    const form = this.filterForm(filterKey)
    Object.keys(form.controls).forEach((id) => {
      form.get(id).setValue(false, { emitEvent: false })
    })
    this.setSelectedIds(filterKey, [])
  }

  private selectedIds(filterKey: OpenRegistrationFilterKey): string[] {
    const selectedIds: Record<OpenRegistrationFilterKey, string[]> = {
      sport: this.selectedSportIds,
      organization: this.selectedOrganizationIds,
      type: this.selectedTypeIds,
      person: this.selectedPersonIds,
    }
    return selectedIds[filterKey]
  }

  private selectedIdsChanged(previousIds: string[], nextIds: string[]): boolean {
    return previousIds.length !== nextIds.length ||
      previousIds.some((id, index) => id !== nextIds[index])
  }

  private selectionChanged(
    previousSportIds: string[],
    previousOrganizationIds: string[],
    previousTypeIds: string[],
    previousPersonIds: string[]
  ): boolean {
    return this.selectedIdsChanged(previousSportIds, this.selectedSportIds) ||
      this.selectedIdsChanged(previousOrganizationIds, this.selectedOrganizationIds) ||
      this.selectedIdsChanged(previousTypeIds, this.selectedTypeIds) ||
      this.selectedIdsChanged(previousPersonIds, this.selectedPersonIds)
  }

  private emitFilters(): void {
    this.filtersChange.emit({
      sport: [...this.selectedSportIds],
      type: [...this.selectedTypeIds],
      organization: [...this.selectedOrganizationIds],
      person: [...this.selectedPersonIds],
    })
  }

  private emitFilterAction(
    action: OpenRegistrationFilterAction['action'],
    filterKey: OpenRegistrationFilterKey
  ): void {
    this.filterAction.emit({ action, filterKey })
  }

  private async loadTranslations(): Promise<void> {
    this.translationsLoaded = await this.translationsLoader.load(this.translationsPath)
  }
}
